import { useEffect, useState } from 'react';
import { showNotification } from '../components/molecules/Notifications';
import { useCheckoutState } from './checkoutState';
import { config } from './config';
import useCss from './useCss';
import useDeepCompare from './useDeepCompare';
import useScript from './useScript';

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    unzer: any;
    unzerCard: any;
    unzerIdeal: any;
    unzerInstallment: any;
  }
}
type UnzerInstance = {
  Card: any;
  Ideal: any;
  PaylaterInstallment: any;
};

// https://docs.unzer.com/online-payments/ui-component/
const useUnzer = (paymentOptionKey: string) => {
  const { checkoutState } = useCheckoutState();
  const selectedOption = useDeepCompare(checkoutState.orderState.payment?.selected?.[0]);
  const [isLoading, setLoading] = useState(true);
  const [unzerInstance, setUnzerInstance] = useState<UnzerInstance>();
  const isRecurring = !!selectedOption?.recurringPaymentOptionId;
  const unzerUrl = 'https://static.unzer.com/v1/unzer.js';
  const scriptLoadingPromiseUnzer = useScript(unzerUrl);
  useCss('https://static.unzer.com/v1/unzer.css');

  useEffect(() => {
    const genericKey = selectedOption?.paymentOptionKey;
    const recurringKey = selectedOption?.recurringPaymentOptionId;
    const optionMismatch = (!isRecurring ? genericKey : recurringKey) !== paymentOptionKey;

    if (optionMismatch || !selectedOption?.configuration?.initializationData?.clientKey) {
      setLoading(false);
      return;
    }

    setLoading(true);
    (async () => {
      try {
        await scriptLoadingPromiseUnzer.current;
        const unzer = new window.unzer(selectedOption?.configuration?.initializationData.clientKey, {
          locale: config.locale.replace('_', '-')
        });

        if (unzer) {
          setUnzerInstance(unzer);
        }
      } catch (error) {
        console.error(error);
      }
    })();
    setLoading(false);
  }, [selectedOption]);

  useEffect(
    () => () => {
      const unzerScript = document.head.querySelectorAll(`script[src='${unzerUrl}']`)[0];
      if (unzerScript) {
        document.head.removeChild(unzerScript);
      }
      setUnzerInstance(undefined);
    },
    []
  );

  return { isLoading, unzerInstance };
};

export default useUnzer;

export const useUnzerResource = () => {
  const createResource = async (selectedPaymentOptionKey: string) => {
    const UNZER_WINDOW: Record<string, any> = {
      unzer_creditcard: window.unzerCard,
      unzer_ideal: window.unzerIdeal,
      unzer_installment: window.unzerInstallment
    };
    try {
      const result = await UNZER_WINDOW[selectedPaymentOptionKey].createResource();

      return result.id;
    } catch (e: any) {
      console.error(e);
      showNotification({
        level: 'error',
        text: e.message,
        autoClose: true,
        closable: true
      });
      return '';
    }
  };

  return { createResource };
};
